import { useEffect, useMemo, useState } from "react";
import { Boxes, Save, Search, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import { formatDateEU } from "@/lib/constants";

const CATEGORY_COLORS = {
  Horeca: "#E85D04",
  Retail: "#2A9D8F",
  "Dry Chiles": "#7B6DCC",
  "Corn Flour": "#C9A02A",
};

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});  // {id: {stock_units, low_stock_threshold}}
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [meta, setMeta] = useState(null);

  const load = async () => {
    const [inv, m] = await Promise.all([api.listInventory(), api.getMeta()]);
    setItems(inv);
    setMeta(m);
    setDrafts({});
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const s = search.toLowerCase();
      const match = !s
        || it.product_name?.toLowerCase().includes(s)
        || it.sku?.toLowerCase().includes(s);
      const cat = catFilter === "all" || it.product_category === catFilter;
      return match && cat;
    });
  }, [items, search, catFilter]);

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const save = async (item) => {
    const draft = drafts[item.id] || {};
    // stock_units is automated — only the threshold is editable
    const payload = {
      product_id: item.product_id,
      stock_units: item.stock_units,  // unchanged
      low_stock_threshold: draft.low_stock_threshold ?? item.low_stock_threshold,
    };
    try {
      await api.updateInventory(item.id, payload);
      toast.success(`${item.product_name} threshold updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  const totalUnits = items.reduce((a, i) => a + (Number(i.stock_units) || 0), 0);
  const lowStockCount = items.filter((i) => i.low_stock_threshold > 0 && i.stock_units < i.low_stock_threshold).length;

  return (
    <div data-testid="page-inventory">
      <PageHeader
        overline="Stock (Automated)"
        title="Inventory"
        subtitle={`${totalUnits.toFixed(0)} total units across ${items.length} SKUs · ${lowStockCount} low-stock alert${lowStockCount === 1 ? "" : "s"} · Changes via Production / Orders / Samples / Waste only`}
        actions={<ExportButton module="inventory" />}
      />

      <div className="card-soft p-4 mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU or product name"
            className="pl-9 bg-white border-[#E5E5E5]"
            data-testid="inventory-search"
          />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-full sm:w-52 bg-white border-[#E5E5E5]" data-testid="inventory-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {meta.product_categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Boxes} title="No SKUs in inventory" description="Add products in the Products page to populate inventory automatically." />
      ) : (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F1E8] text-[#1F4D3A] text-[10px] uppercase tracking-wider font-bold">
              <tr>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">SKU</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Format</th>
                <th className="text-left px-4 py-3">Stock</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Low Alert</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Updated</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const draft = drafts[it.id] || {};
                const stock = draft.stock_units ?? it.stock_units;
                const low = draft.low_stock_threshold ?? it.low_stock_threshold;
                const isLow = low > 0 && stock < low;
                const dirty = drafts[it.id] !== undefined;
                return (
                  <tr key={it.id} className="border-t border-[#E5E5E5] hover:bg-[#F7F1E8]/30" data-testid={`inventory-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
                          style={{ backgroundColor: `${CATEGORY_COLORS[it.product_category] || "#6C757D"}1f`, color: CATEGORY_COLORS[it.product_category] || "#6C757D" }}
                        >
                          {it.product_category}
                        </span>
                        <span className="font-semibold text-[#222]">{it.product_name}</span>
                        {isLow && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E76F51]/15 text-[#E76F51] font-bold">
                            <AlertTriangle size={10} /> Low
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#6C757D] hidden sm:table-cell">{it.sku}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{it.product_format || "—"}</td>
                    <td className="px-4 py-3">
                      <div
                        className="w-24 h-9 inline-flex items-center justify-center rounded-md bg-[#F7F1E8] border border-[#E5E5E5] text-[#1F4D3A] font-display font-bold"
                        data-testid={`inventory-stock-${it.id}`}
                        title="Stock is automated — changes flow from Production / Orders / Samples / Waste"
                      >
                        {Number(it.stock_units || 0).toFixed(0)}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <Input
                        type="number" min="0" step="1" value={low === 0 ? "" : low}
                        onChange={(e) => setDraft(it.id, { low_stock_threshold: e.target.value === "" ? 0 : Number(e.target.value) })}
                        className="w-24 h-9"
                        data-testid={`inventory-low-${it.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6C757D] hidden lg:table-cell">{formatDateEU(it.updated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant={dirty ? "default" : "ghost"}
                        onClick={() => save(it)}
                        disabled={!dirty}
                        className={dirty ? "bg-[#E85D04] hover:bg-[#c94b03] text-white" : ""}
                        data-testid={`save-inventory-${it.id}`}
                      >
                        <Save size={14} className="mr-1" /> Save
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
